import React, { useState } from "react";
import { Box, Typography, Breadcrumbs, Link, Alert, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCreateCompanyGroup } from "../../hooks/useCompanyGroup";
import CompanyGroupForm from "../../components/companyGroup/CompanyGroupForm";

const CompanyGroupCreate: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const createMutation = useCreateCompanyGroup();

	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");
	const [alertSeverity, setAlertSeverity] = useState<"success" | "error">("success");

	const handleCreate = async (data: { name: string; website?: string; companyIds: number[] }) => {
		try {
			await createMutation.mutateAsync({
				name: data.name,
				website: data.website,
				companyIds: data.companyIds.length > 0 ? data.companyIds : undefined,
			});

			setAlertSeverity("success");
			setAlertMessage(t("companyGroup.createdSuccessfully"));
			setAlertOpen(true);

			setTimeout(() => {
				navigate("/company-groups");
			}, 1500);
		} catch (error: any) {
			setAlertSeverity("error");
			const msg = error?.message || "";
			if (msg.includes("already exists")) {
				setAlertMessage(t("companyGroup.nameAlreadyExists", { name: data.name }));
			} else if (msg.includes("already belongs to another group")) {
				setAlertMessage(t("companyGroup.companyAlreadyInOtherGroup"));
			} else {
				setAlertMessage(t("companyGroup.creationError"));
			}
			setAlertOpen(true);
		}
	};

	return (
		<Box p={3}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<Typography variant="h2">{t("companyGroup.createTitle")}</Typography>
			</Box>

			<Breadcrumbs separator=">" aria-label="breadcrumb" sx={{ mb: 3 }}>
				<Link
					underline="hover"
					color="inherit"
					onClick={() => navigate("/company-groups")}
					sx={{ cursor: "pointer" }}
				>
					{t("companyGroup.list")}
				</Link>
				<Typography color="text.primary">{t("companyGroup.createNew")}</Typography>
			</Breadcrumbs>

			<CompanyGroupForm
				onSubmit={handleCreate}
				isSubmitting={createMutation.isPending}
				submitButtonText={t("companyGroup.create")}
			/>

			<Snackbar
				open={alertOpen}
				autoHideDuration={5000}
				onClose={() => setAlertOpen(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setAlertOpen(false)}
					severity={alertSeverity}
					variant="filled"
					sx={{ width: "100%" }}
				>
					{alertMessage}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default CompanyGroupCreate;
